import Loader from "@qpr/client-api-base/ui/components/Loader";
import { FieldValue } from "backend/datainput";
import useComments from "backend/hooks/comments/useComments";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useAttachments from "backend/hooks/files/useAttachments";
import { ICardElementWithChildren } from "backend/model/model";
import ValueInputForm from "design/components/forms/ValueInputForm";
import { t } from "i18next";
import React from "react";
import { getDateFromMEAValue } from "utils/utils";
import * as validators from "./validator";
import validateInputForm from "./validator/validateInputForm";

const InitiativeInputForm = (props: {
  item: ICardElementWithChildren;
  onSubmit?: (values: FieldValue[]) => void;
  onSave?: (values: FieldValue[]) => void;
  isReadOnly?: boolean;
}) => {
  const { item: selectedRow } = props;
  const { data: comments, isLoading } = useComments({
    id: selectedRow?.workflowInstance?.meaId || "",
    period: selectedRow?.workflowInstance?.period || "",
  });

  const { data: children, isLoading: isLoadingChildren } = useHierarchy(
    props.item.id,
    undefined,
    selectedRow.workflowInstance?.period,
    props.item.childobjects ? false : true // disable query if no childobjects
  );
  const { data: attachments, isLoading: isLoadingAttachments } = useAttachments(
    { elementId: props.item.id, period: props.item.workflowInstance?.period }
  );
  const [milestoneValues, setMilestoneValues] = React.useState<FieldValue[]>(
    []
  );
  const milestones = React.useMemo(() => {
    return (children || []).filter((item) => item.typename === "Milestone");
  }, [children]);
  if (isLoading || isLoadingChildren || isLoadingAttachments) return <Loader />;

  const getDateValue = (value?: Date) => {
    if (!value) return "";
    return `${value.getFullYear()}-${(value.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${value.getDate().toString().padStart(2, "0")}`;
  };

  if (isLoadingChildren) return <Loader />;

  const buildAllValues = (values: FieldValue[]) => {
    return [...values, ...milestoneValues].map((v) => {
      if (v.field.element) {
        v.field.element.workflowInstance = props.item.workflowInstance;
      }
      return v;
    });
  };

  return (
    <>
      <ValueInputForm
        buttons={[
          {
            tag: "save",
            style: "save",
            text: t("workflowinput.form.Save").toString(),
            onClick: (values) => {
              if (props.onSave) {
                props.onSave(buildAllValues(values));
              }
            },
          },
          {
            tag: "submit",
            style: "submit",
            text: t("workflowinput.form.Submit").toString(),
            onClick: (values) => {
              console.log("initiative values", values);
              if (milestones.length > 0 && milestoneValues.length === 0) {
                return;
              }

              if (props.onSubmit) {
                const allValues = buildAllValues(values);
                console.log("allValues", allValues);
                props.onSubmit(allValues);
              }
            },
          },
        ]}
        key={selectedRow?.workflowInstance?.wfInstanceId}
        element={selectedRow!}
        onIntermediateChange={(values) => {
          console.log(values);
        }}
        fields={[
          {
            title: t("workflowinput.form.ACTUAL_END_DATE").toString(),
            series: "ACTUAL_END_DATE",
            element: selectedRow,
            inputType: "datetime",
            initialValue: selectedRow.ACTUAL_END_DATE
              ? getDateValue(getDateFromMEAValue(selectedRow.ACTUAL_END_DATE))
              : undefined,
            isDisabled: props.isReadOnly,
            validate: (state) => {
              return validateInputForm(state, [validators.isNotEmpty]);
            },
          },
          {
            title: t("workflowinput.form.COMPLETION_PERCENTAGE").toString(),
            series: "COMPLETION_PERCENTAGE",
            element: selectedRow,
            inputType: "string",
            unitSeries: "COMPLETION_PERCENTAGE_unit",
            initialValue: selectedRow.COMPLETION_PERCENTAGE,
            isDisabled: props.isReadOnly,
            validate: (state) => {
              return validateInputForm(state, [
                validators.isNotEmpty,
                validators.isNumber,
              ]);
            },
          },
          {
            title: t("workflowinput.form.EXPENDITURE").toString(),
            series: "EXPENDITURE",
            element: selectedRow,
            inputType: "string",
            unitSeries: "EXPENDITURE_unit",
            isDisabled: props.isReadOnly,
            validate: (state) => {
              return validateInputForm(state, [
                validators.isNotEmpty,
                validators.isNumber,
              ]);
            },
          },
          {
            inputType: "info",
            isHidden: milestones.length === 0,
            title: (
              <div>
                {(milestones || []).map((item) => (
                  <ValueInputForm
                    key={item.id}
                    onIntermediateChange={(values) => {
                      setMilestoneValues((prevValues) => {
                        return [
                          ...prevValues.filter(
                            (v) => v.field.element?.id !== item.id
                          ),
                          ...values,
                        ];
                      });
                    }}
                    buttons={[]}
                    element={item}
                    fields={[
                      {
                        inputType: "info",
                        title: <h4>{item.name}</h4>,
                      },
                      {
                        title: t(
                          "workflowinput.form.ACTUAL_END_DATE"
                        ).toString(),
                        series: "ACTUAL_END_DATE",
                        element: item,
                        inputType: "datetime",
                        initialValue: item.ACTUAL_END_DATE
                          ? getDateValue(
                              getDateFromMEAValue(item.ACTUAL_END_DATE)
                            )
                          : undefined,
                        isDisabled: props.isReadOnly,
                        validate: (state) => {
                          return validateInputForm(state, [
                            validators.isNotEmpty,
                          ]);
                        },
                      },
                      {
                        title: t(
                          "workflowinput.form.COMPLETION_PERCENTAGE"
                        ).toString(),
                        series: "COMPLETION_PERCENTAGE",
                        element: item,
                        inputType: "string",
                        unitSeries: "COMPLETION_PERCENTAGE_unit",
                        initialValue: item.COMPLETION_PERCENTAGE,
                        isDisabled: props.isReadOnly,
                        validate: (state) => {
                          return validateInputForm(state, [
                            validators.isNotEmpty,
                            validators.isNumber,
                          ]);
                        },
                      },
                    ]}
                  />
                ))}
              </div>
            ),
          },
          {
            title: t("workflowinput.form.Attachments"),
            element: selectedRow,
            inputType: "attachments",
            initialFiles: attachments,
            isDisabled: props.isReadOnly,
          },
          {
            title: t("workflowinput.form.Actions and Recommendations"),
            element: selectedRow,
            inputType: "comment",
            comments: comments,
            isDisabled: props.isReadOnly,
            initialValue:
              comments && comments[0] ? comments[0].description : "",
          },
        ]}
      />
    </>
  );
};

export default InitiativeInputForm;
